import React, { useState, useCallback } from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  Slider,
  IconButton,
} from "@mui/material";
import Cropper from "react-easy-crop";
import type { Area, Point } from "react-easy-crop";
import CropIcon from "@mui/icons-material/Crop";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { getCroppedImg } from "./cropHelpers";

interface CropModalProps {
  open: boolean;
  imageSrc: string | null;
  onClose: () => void;
  onCropComplete: (croppedFile: File) => void;
}

const CropModal: React.FC<CropModalProps> = ({ open, imageSrc, onClose, onCropComplete }) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);

  const onCropChange = useCallback((location: Point) => setCrop(location), []);
  const onZoomChange = useCallback((z: number) => setZoom(z), []);

  const onCropAreaComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setSaving(true);
    try {
      const croppedFile = await getCroppedImg(imageSrc, croppedAreaPixels, 500, 0.8);
      onCropComplete(croppedFile);
      handleClose();
    } catch {
      // Silently fail — parent can show error
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    onClose();
  };

  if (!imageSrc) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px", overflow: "hidden" } }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CropIcon sx={{ fontSize: 20, color: "primary.main" }} />
          <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Crop Photo</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Cropper */}
      <Box sx={{ position: "relative", width: "100%", pt: "100%", bgcolor: "#1a1a1a" }}>
        <Box sx={{ position: "absolute", inset: 0 }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropAreaComplete}
          />
        </Box>
      </Box>

      {/* Zoom Control */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 3, py: 1.5 }}>
        <ZoomInIcon sx={{ fontSize: 18, color: "text.secondary" }} />
        <Slider
          value={zoom}
          min={1}
          max={3}
          step={0.05}
          onChange={(_, v) => setZoom(v as number)}
          sx={{ flex: 1 }}
          size="small"
        />
        <Typography sx={{ fontSize: 12, color: "text.secondary", minWidth: 36, textAlign: "right" }}>
          {zoom.toFixed(1)}x
        </Typography>
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 2.5, pb: 2.5, pt: 0.5 }}>
        <Button variant="outlined" onClick={handleClose} sx={{ borderRadius: "10px", textTransform: "none", fontSize: 13 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<CropIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ borderRadius: "10px", textTransform: "none", fontSize: 13, boxShadow: "none" }}
        >
          {saving ? "Processing..." : "Crop & Save"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default CropModal;
